import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import { API_BASE_URL, FONT_UI } from "../layouts/constants";

function getToken() {
  return (
    localStorage.getItem("guardToken") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("adminToken")
  );
}

function isGuardSession() {
  return localStorage.getItem("isGuardLoggedIn") === "true";
}

/** Public image URL — no Authorization on <img>. Requires public /uploads. */
function resolveQrImageUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const value = String(pathOrUrl).trim();
  if (!value) return "";
  if (value.startsWith("blob:")) return value;

  const apiOrigin = String(API_BASE_URL || "")
    .replace(/\/api\/?$/, "")
    .replace(/\/$/, "");

  if (value.startsWith("http://") || value.startsWith("https://")) {
    try {
      const u = new URL(value);
      const idx = u.pathname.indexOf("/uploads/");
      if (idx !== -1) {
        return `${apiOrigin}${u.pathname.substring(idx)}${u.search || ""}`;
      }
      return value;
    } catch {
      return value;
    }
  }

  const idx = value.indexOf("uploads/");
  if (idx !== -1) {
    const part = value.startsWith("/")
      ? value.substring(value.indexOf("/uploads/"))
      : `/${value.substring(idx)}`;
    return `${apiOrigin}${part}`;
  }

  const clean = value.startsWith("/") ? value : `/${value}`;
  return `${apiOrigin}${clean}`;
}

export default function SentinelQRCodePage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [checkpoints, setCheckpoints] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [nfcItems, setNfcItems] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedRoute, setSelectedRoute] = useState("all");
  const [qrImages, setQrImages] = useState({});
  const [loadingQr, setLoadingQr] = useState({});
  const errorShownRef = useRef(false);

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const isGuardLoggedIn = localStorage.getItem("isGuardLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const guardData = (() => {
      try {
        return JSON.parse(localStorage.getItem("guardData") || "{}");
      } catch {
        return {};
      }
    })();

    if (!token || (!isLoggedIn && !isGuardLoggedIn)) {
      navigate("/");
      return;
    }

    const local = isGuardSession()
      ? {
          company_name:
            guardData.fullName ||
            guardData.name ||
            guardData.fld_name ||
            localStorage.getItem("guardName") ||
            "Guard",
          licensedFor: "Guard QR Access",
          admin_active: 1,
          company_address: "",
          company_phone: "",
          admin_email:
            guardData.email ||
            guardData.fld_mail ||
            localStorage.getItem("guardEmail") ||
            "",
          company_logo: "",
          user_map: localStorage.getItem("user_map") || "",
        }
      : {
          company_name:
            localStorage.getItem("company_name") || userData.company_name || "",
          licensedFor:
            localStorage.getItem("licensedFor") || userData.licensedFor || "",
          admin_active: Number(
            localStorage.getItem("admin_active") ?? userData.admin_active ?? 0
          ),
          company_address:
            localStorage.getItem("company_address") ||
            userData.company_address ||
            "",
          company_phone:
            localStorage.getItem("company_phone") ||
            userData.company_phone ||
            "",
          admin_email:
            localStorage.getItem("admin_email") || userData.admin_email || "",
          company_logo:
            localStorage.getItem("company_logo") || userData.company_logo || "",
          user_map: localStorage.getItem("user_map") || userData.user_map || "",
        };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  const normalizeQRList = (result) => {
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result)) return result;
    return [];
  };

  const fetchQRImage = async (qrPath, id) => {
    if (!qrPath || !id) return;
    try {
      setLoadingQr((prev) => ({ ...prev, [id]: true }));
      const imageUrl = resolveQrImageUrl(qrPath);
      setQrImages((prev) => ({ ...prev, [id]: imageUrl }));
    } catch (error) {
      console.error(`Error resolving QR for ${id}:`, error);
      setQrImages((prev) => ({ ...prev, [id]: resolveQrImageUrl(qrPath) }));
    } finally {
      setLoadingQr((prev) => ({ ...prev, [id]: false }));
    }
  };

  const fetchPageData = async (local, token) => {
    try {
      setLoading(true);
      errorShownRef.current = false;

      const guardSession = isGuardSession();

      const dashboardPromise = guardSession
        ? Promise.resolve(null)
        : fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });

      const [dashboardRes, checkpointRes, routeRes, nfcRes] = await Promise.all(
        [
          dashboardPromise,
          fetch(`${API_BASE_URL}/qrcode/sentinel/checkpoints`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }),
          fetch(`${API_BASE_URL}/qrcode/sentinel/routes`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }),
          fetch(`${API_BASE_URL}/qrcode/sentinel/nfc`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }),
        ]
      );

      const checkpointResult = await checkpointRes.json();
      const routeResult = await routeRes.json();
      const nfcResult = await nfcRes.json().catch(() => ({ data: [] }));

      if (!checkpointRes.ok || checkpointResult.success === false) {
        throw new Error(
          checkpointResult.message || "Failed to fetch checkpoint QR"
        );
      }

      if (!routeRes.ok || routeResult.success === false) {
        throw new Error(routeResult.message || "Failed to fetch route QR");
      }

      const nfcList =
        nfcRes.ok && nfcResult.success !== false
          ? normalizeQRList(nfcResult)
          : [];

      if (!guardSession && dashboardRes) {
        const dashboardResult = await dashboardRes.json();

        if (!dashboardRes.ok || dashboardResult.success === false) {
          throw new Error(
            dashboardResult.message || "Failed to fetch dashboard"
          );
        }

        const companyDetails = dashboardResult?.data?.companyDetails || {};

        setDashboardData({
          ...local,
          company_name: companyDetails.company_name ?? local.company_name ?? "",
          licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
          admin_active: Number(
            companyDetails.admin_active ?? local.admin_active ?? 0
          ),
          company_address:
            companyDetails.company_address ?? local.company_address ?? "",
          company_phone:
            companyDetails.company_phone ?? local.company_phone ?? "",
          admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
          company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
          user_map: companyDetails.user_map ?? local.user_map ?? "",
        });
      }

      const checkpointList = normalizeQRList(checkpointResult);
      const routeList = normalizeQRList(routeResult);

      setCheckpoints(checkpointList);
      setRoutes(routeList);
      setNfcItems(nfcList);

      [...checkpointList, ...routeList].forEach((item) => {
        const qrPath = item.qr_image_url || item.qr_code_id || item.qr_image;
        if (qrPath && item.id) fetchQRImage(qrPath, item.id);
      });

      toast.success("Sentinel QR codes loaded successfully!");
    } catch (error) {
      console.error("Sentinel QR page error:", error);
      toast.error(error.message || "Failed to load sentinel QR codes.");
      errorShownRef.current = true;
    } finally {
      setLoading(false);
    }
  };

  const routePage = useMemo(
    () =>
      dashboardData.user_map === "google" ? "/route-master" : "/route-master1",
    [dashboardData.user_map]
  );

  const stats = useMemo(
    () => ({
      checkpointCount: checkpoints.length,
      routeCount: routes.length,
      nfcCount: nfcItems.length,
      totalCount: checkpoints.length + routes.length + nfcItems.length,
    }),
    [checkpoints, routes, nfcItems]
  );

  const routeOptions = useMemo(() => {
    const map = new Map();
    const add = (item) => {
      const data = item.qr_data || {};
      const id = String(data.route_id || item.reference_id || item.id || "");
      const name = data.route_name || item.reference_name || id;
      if (id && !map.has(id)) map.set(id, name);
    };
    routes.forEach(add);
    checkpoints.forEach(add);
    nfcItems.forEach(add);
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [routes, checkpoints, nfcItems]);

  const textFilter = (list) => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return list;
    return list.filter((item) => {
      const data = item.qr_data || {};
      return (
        String(item.reference_id || "").toLowerCase().includes(keyword) ||
        String(item.reference_name || "").toLowerCase().includes(keyword) ||
        String(item.qr_type || "").toLowerCase().includes(keyword) ||
        String(data.route_id || "").toLowerCase().includes(keyword) ||
        String(data.route_name || "").toLowerCase().includes(keyword) ||
        String(data.checkpoint_id || "").toLowerCase().includes(keyword) ||
        String(data.checkpoint_name || "").toLowerCase().includes(keyword) ||
        String(data.nfc_uid || "").toLowerCase().includes(keyword) ||
        String(data.nfc_token || "").toLowerCase().includes(keyword) ||
        String(data.from || "").toLowerCase().includes(keyword) ||
        String(data.to || "").toLowerCase().includes(keyword)
      );
    });
  };

  const textFilteredCheckpoints = useMemo(
    () => textFilter(checkpoints),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkpoints, search]
  );
  const textFilteredRoutes = useMemo(
    () => textFilter(routes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [routes, search]
  );
  const textFilteredNfc = useMemo(
    () => textFilter(nfcItems),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nfcItems, search]
  );

  const groupedByRoute = useMemo(() => {
    const groups = new Map();
    const ensure = (routeId, routeName) => {
      if (!groups.has(routeId)) {
        groups.set(routeId, {
          routeId,
          routeName,
          routeQR: null,
          checkpointQRs: [],
          nfcItems: [],
        });
      }
      return groups.get(routeId);
    };

    textFilteredRoutes.forEach((item) => {
      const data = item.qr_data || {};
      const routeId = String(
        data.route_id || item.reference_id || item.id || "unknown"
      );
      const routeName = data.route_name || item.reference_name || routeId;
      ensure(routeId, routeName).routeQR = item;
    });

    textFilteredCheckpoints.forEach((item) => {
      const data = item.qr_data || {};
      const routeId = String(data.route_id || "unknown");
      const routeName = data.route_name || routeId;
      ensure(routeId, routeName).checkpointQRs.push(item);
    });

    textFilteredNfc.forEach((item) => {
      const data = item.qr_data || {};
      const routeId = String(data.route_id || "unknown");
      const routeName = data.route_name || routeId;
      ensure(routeId, routeName).nfcItems.push(item);
    });

    return Array.from(groups.values()).sort((a, b) =>
      a.routeName.localeCompare(b.routeName)
    );
  }, [textFilteredRoutes, textFilteredCheckpoints, textFilteredNfc]);

  const visibleGroups = useMemo(() => {
    if (selectedRoute === "all") return groupedByRoute;
    return groupedByRoute.filter((g) => g.routeId === selectedRoute);
  }, [groupedByRoute, selectedRoute]);

  const downloadQR = async (qrPath, fileName) => {
    if (!qrPath) {
      toast.error("No QR image available to download.");
      return;
    }

    try {
      const token = getToken();
      const relativePath = (() => {
        const resolved = resolveQrImageUrl(qrPath);
        const idx = resolved.indexOf("/uploads/");
        if (idx !== -1) return resolved.substring(idx);
        if (String(qrPath).includes("uploads/")) {
          const i = String(qrPath).indexOf("uploads/");
          return `/${String(qrPath).substring(i)}`;
        }
        return qrPath;
      })();

      const response = await fetch(
        `${API_BASE_URL}/qrcode/download?path=${encodeURIComponent(
          relativePath
        )}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        const direct = resolveQrImageUrl(qrPath);
        const directRes = await fetch(direct);
        if (!directRes.ok) {
          throw new Error(`Failed to download: ${response.status}`);
        }
        const blob = await directRes.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `${fileName}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        toast.success(`QR code "${fileName}" downloaded successfully!`);
        return;
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${fileName}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`QR code "${fileName}" downloaded successfully!`);
    } catch (error) {
      console.error("QR download error:", error);
      toast.error("Failed to download QR image.");
    }
  };

  const handleRefresh = async () => {
    const token = getToken();
    await fetchPageData(dashboardData, token);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading sentinel QR codes...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="sentinel-qr-code"
      activeMenu="sentinel-qr-code"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: { primary: "#22c55e", secondary: "white" },
          },
          error: {
            iconTheme: { primary: "#ef4444", secondary: "white" },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Sentinel QR Code
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          View and download sentinel route, checkpoint QR images, and NFC
          checkpoints
        </p>

        {/* Stats — same template as Patrol */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-[18px]">
          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--btn-bg)] border border-[var(--surface-border)] shadow-[0_4px_18px_var(--accent-glow)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--btn-text)] opacity-75 m-0 mb-1.5">
              Checkpoint QR Codes
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--btn-text)]">
              {stats.checkpointCount}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Route QR Codes
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {stats.routeCount}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              NFC Checkpoints
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {stats.nfcCount}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Total
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {stats.totalCount}
            </p>
          </div>
        </div>

        {/* Filter panel */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="flex justify-between items-center gap-4 flex-wrap mb-5">
            <div>
              <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Sentinel QR Management
              </h3>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Filter by route, then search within results
              </p>
            </div>

            <button
              type="button"
              onClick={handleRefresh}
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Refresh
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={selectedRoute}
              onChange={(e) => setSelectedRoute(e.target.value)}
              className="sm:w-64 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
              style={{ fontFamily: FONT_UI }}
            >
              <option value="all">All Routes</option>
              {routeOptions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Search by QR type, route, checkpoint, NFC UID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
              style={{ fontFamily: FONT_UI }}
            />
          </div>
        </div>

        {visibleGroups.length === 0 ? (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] text-center">
            <p className="text-[13px] text-[var(--text-muted)] m-0">
              No QR / NFC items found for the selected filter.
            </p>
          </div>
        ) : (
          visibleGroups.map((group) => (
            <RouteGroup
              key={group.routeId}
              group={group}
              onDownload={downloadQR}
              qrImages={qrImages}
              loadingQr={loadingQr}
            />
          ))
        )}
      </div>
    </Layout>
  );
}

function RouteGroup({ group, onDownload, qrImages, loadingQr }) {
  const { routeName, routeQR, checkpointQRs, nfcItems } = group;

  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-px flex-1 bg-[var(--divider)]" />
        <span
          className="px-4 py-1.5 rounded-full bg-[var(--btn-bg)] text-[var(--btn-text)] text-sm font-extrabold whitespace-nowrap"
          style={{ fontFamily: FONT_UI }}
        >
          {routeName}
        </span>
        <div className="h-px flex-1 bg-[var(--divider)]" />
      </div>

      {routeQR && (
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-4">
          <h3 className="text-[18px] font-extrabold text-[#22c55e] m-0 mb-5">
            Route QR
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            <QRCard
              item={routeQR}
              type="route"
              onDownload={onDownload}
              qrImages={qrImages}
              loadingQr={loadingQr}
            />
          </div>
        </div>
      )}

      {checkpointQRs.length > 0 && (
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-4">
          <h3 className="text-[18px] font-extrabold text-[var(--accent)] m-0 mb-5">
            Checkpoint QRs
            <span className="ml-2 px-2.5 py-0.5 rounded-full bg-[var(--accent-faint)] text-[var(--accent)] text-xs font-extrabold">
              {checkpointQRs.length}
            </span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {checkpointQRs.map((item, index) => (
              <QRCard
                key={`cp-${item.id || index}`}
                item={item}
                type="checkpoint"
                onDownload={onDownload}
                qrImages={qrImages}
                loadingQr={loadingQr}
              />
            ))}
          </div>
        </div>
      )}

      {nfcItems.length > 0 && (
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <h3 className="text-[18px] font-extrabold text-[#a855f7] m-0 mb-5">
            NFC Checkpoints
            <span className="ml-2 px-2.5 py-0.5 rounded-full bg-[rgba(168,85,247,0.12)] text-[#a855f7] text-xs font-extrabold">
              {nfcItems.length}
            </span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {nfcItems.map((item, index) => (
              <QRCard
                key={`nfc-${item.id || index}`}
                item={item}
                type="nfc"
                onDownload={onDownload}
                qrImages={qrImages}
                loadingQr={loadingQr}
              />
            ))}
          </div>
        </div>
      )}

      {!routeQR && checkpointQRs.length === 0 && nfcItems.length === 0 && (
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] text-center">
          <p className="text-[13px] text-[var(--text-muted)] m-0">
            No QR / NFC items available for this route.
          </p>
        </div>
      )}
    </div>
  );
}

function QRCard({ item, type, onDownload, qrImages, loadingQr }) {
  const data = item.qr_data || {};

  const id =
    type === "checkpoint" || type === "nfc"
      ? data.checkpoint_id || item.reference_id || item.id
      : data.route_id || item.reference_id || item.id;

  const name =
    type === "checkpoint" || type === "nfc"
      ? data.checkpoint_name || item.reference_name || "-"
      : data.route_name || item.reference_name || "-";

  const routeId = data.route_id || item.reference_id || "-";
  const routeName = data.route_name || "-";
  const checkpointId = data.checkpoint_id || item.reference_id || "-";
  const checkpointName = data.checkpoint_name || item.reference_name || "-";
  const lat = data.lat ?? data.latitude ?? "-";
  const lng = data.lng ?? data.longitude ?? "-";
  const nfcUid = data.nfc_uid || "-";
  const nfcToken = data.nfc_token || "-";
  const from = data.from || "-";
  const to = data.to || "-";
  const fromLat = data.from_lat ?? "-";
  const fromLng = data.from_lng ?? "-";
  const toLat = data.to_lat ?? "-";
  const toLng = data.to_lng ?? "-";
  const clientId = data.client_id ?? "-";

  const qrPath = item.qr_image_url || item.qr_code_id || item.qr_image || "";
  const isLoading = loadingQr[item.id] || false;
  const imageSrc = qrImages[item.id] || resolveQrImageUrl(qrPath) || "";

  const badgeClass =
    type === "checkpoint"
      ? "bg-[rgba(59,130,246,0.12)] border border-[rgba(59,130,246,0.28)] text-[#3b82f6]"
      : type === "nfc"
        ? "bg-[rgba(168,85,247,0.12)] border border-[rgba(168,85,247,0.28)] text-[#a855f7]"
        : "bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.28)] text-[#22c55e]";

  const subtitle =
    type === "checkpoint"
      ? "Sentinel Checkpoint QR"
      : type === "nfc"
        ? "Sentinel NFC Checkpoint"
        : "Sentinel Route QR";

  return (
    <div className="bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-2xl p-5 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors">
      <div className="flex justify-between gap-3 mb-4">
        <div>
          <h4 className="m-0 text-lg font-extrabold text-[var(--text-primary)]">
            {name}
          </h4>
          <p className="mt-1 text-sm text-[var(--text-muted)]">{subtitle}</p>
        </div>
        <span
          className={`h-fit px-3 py-1.5 rounded-full text-xs font-extrabold ${badgeClass}`}
        >
          {item.qr_type || type}
        </span>
      </div>

      <div className="flex justify-center bg-[var(--surface)] rounded-2xl p-4 border border-[var(--surface-border)] mb-4 min-h-[200px]">
        {type === "nfc" && !imageSrc ? (
          <div className="w-[190px] h-[190px] flex flex-col items-center justify-center gap-2 text-[#a855f7]">
            <div className="text-4xl font-extrabold">NFC</div>
            <div className="text-xs text-[var(--text-muted)] break-all px-2 text-center">
              {nfcUid !== "-" ? nfcUid : "UID assigned"}
            </div>
          </div>
        ) : isLoading ? (
          <div className="w-[190px] h-[190px] flex items-center justify-center">
            <div className="w-8 h-8 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
          </div>
        ) : imageSrc ? (
          <img
            src={imageSrc}
            alt={name}
            className="w-[190px] h-[190px] object-contain"
            onError={(e) => {
              const fallback = resolveQrImageUrl(qrPath);
              if (fallback && e.target.src !== fallback) {
                e.target.src = fallback;
              }
            }}
          />
        ) : (
          <div className="w-[190px] h-[190px] flex items-center justify-center text-[var(--text-muted)] text-sm">
            QR image not found
          </div>
        )}
      </div>

      <div className="space-y-1 text-sm text-[var(--text-muted)]">
        <p>
          <b className="text-[var(--text-secondary)]">Record ID:</b>{" "}
          {item.id || "-"}
        </p>
        <p>
          <b className="text-[var(--text-secondary)]">Reference ID:</b>{" "}
          {item.reference_id || "-"}
        </p>
        <p>
          <b className="text-[var(--text-secondary)]">QR Type:</b>{" "}
          {item.qr_type || type}
        </p>

        {type === "checkpoint" && (
          <>
            <p>
              <b className="text-[var(--text-secondary)]">Route ID:</b> {routeId}
            </p>
            <p>
              <b className="text-[var(--text-secondary)]">Route Name:</b>{" "}
              {routeName}
            </p>
            <p>
              <b className="text-[var(--text-secondary)]">Checkpoint ID:</b>{" "}
              {checkpointId}
            </p>
            <p>
              <b className="text-[var(--text-secondary)]">Checkpoint:</b>{" "}
              {checkpointName}
            </p>
            <p>
              <b className="text-[var(--text-secondary)]">Lat / Lng:</b> {lat},{" "}
              {lng}
            </p>
          </>
        )}

        {type === "nfc" && (
          <>
            <p>
              <b className="text-[var(--text-secondary)]">Route ID:</b> {routeId}
            </p>
            <p>
              <b className="text-[var(--text-secondary)]">Route Name:</b>{" "}
              {routeName}
            </p>
            <p>
              <b className="text-[var(--text-secondary)]">Checkpoint ID:</b>{" "}
              {checkpointId}
            </p>
            <p>
              <b className="text-[var(--text-secondary)]">Checkpoint:</b>{" "}
              {checkpointName}
            </p>
            <p>
              <b className="text-[var(--text-secondary)]">NFC UID:</b> {nfcUid}
            </p>
            <p>
              <b className="text-[var(--text-secondary)]">NFC Token:</b>{" "}
              {nfcToken}
            </p>
            <p>
              <b className="text-[var(--text-secondary)]">Lat / Lng:</b> {lat},{" "}
              {lng}
            </p>
          </>
        )}

        {type === "route" && (
          <>
            <p>
              <b className="text-[var(--text-secondary)]">Route ID:</b> {routeId}
            </p>
            <p>
              <b className="text-[var(--text-secondary)]">Route Name:</b>{" "}
              {routeName}
            </p>
            <p>
              <b className="text-[var(--text-secondary)]">Client ID:</b>{" "}
              {clientId}
            </p>
            <p>
              <b className="text-[var(--text-secondary)]">From:</b> {from}
            </p>
            <p>
              <b className="text-[var(--text-secondary)]">From Lat/Lng:</b>{" "}
              {fromLat}, {fromLng}
            </p>
            <p>
              <b className="text-[var(--text-secondary)]">To:</b> {to}
            </p>
            <p>
              <b className="text-[var(--text-secondary)]">To Lat/Lng:</b>{" "}
              {toLat}, {toLng}
            </p>
          </>
        )}
      </div>

      {type !== "nfc" && (
        <button
          type="button"
          disabled={!qrPath}
          onClick={() =>
            onDownload(qrPath, `sentinel_${type}_${id || "item"}`)
          }
          className="mt-4 w-full border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          style={{
            background: "var(--btn-bg)",
            color: "var(--btn-text)",
            boxShadow: "0 4px 18px var(--accent-glow)",
          }}
        >
          Download QR
        </button>
      )}
    </div>
  );
}
